// S4 (v0.15.0) — API token management UI.
//
// Permission narrowing happens server-side: the backend rejects a
// POST that tries to grant permissions the creator doesn't have.
// The catalog below shows every S4 key (minus super_admin); if the
// user picks one they don't have, the API returns 403 with a clear
// message and we surface it inline.
import { useMemo, useState } from "react";
import { CopyIcon, KeyRoundIcon } from "lucide-react";

import {
  useAPITokens,
  useCreateAPIToken,
  usePermissionCatalog,
  useRevokeAPIToken,
} from "@/api/s4";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QueryState } from "@/components/QueryState";
import { formatTimestamp } from "@/utils/datetime";


export default function APITokensPage() {
  // `data: tokens = []` DEFAULTS A FAILURE TO EMPTY. On a 500 this page used to
  // render "No API tokens yet." — a positive claim about a security surface,
  // made on the strength of a request that never landed. An operator reads that
  // as "nothing is holding a machine credential" and stops looking.
  const {
    data: tokens = [],
    refetch,
    isLoading: tokensLoading,
    isError: tokensError,
    error: tokensErrorObj,
  } = useAPITokens();
  const { data: catalog = [] } = usePermissionCatalog();
  const create = useCreateAPIToken();
  const revoke = useRevokeAPIToken();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [perms, setPerms] = useState<Set<string>>(new Set());
  const [expires, setExpires] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [reveal, setReveal] = useState<{ token: string; name: string } | null>(
    null,
  );
  const [copied, setCopied] = useState(false);

  // The S4 catalog includes super_admin — never offer that one in
  // the UI; only super admins should hold tokens with it anyway, and
  // even they can't create one in the API without is_super_admin.
  const grouped = useMemo(() => {
    const out: Record<string, typeof catalog> = {};
    for (const row of catalog) {
      if (row.key === "super_admin") continue;
      (out[row.category] = out[row.category] || []).push(row);
    }
    return out;
  }, [catalog]);

  function toggle(key: string) {
    setPerms((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function handleCreate() {
    setError(null);
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    try {
      const result = await create.mutateAsync({
        name: name.trim(),
        permissions: Array.from(perms),
        expires_at: expires ? new Date(expires).toISOString() : null,
      });
      setOpen(false);
      setReveal({ token: result.token, name: result.name });
      setName("");
      setPerms(new Set());
      setExpires("");
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        .response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed");
    }
  }

  function copyReveal() {
    if (!reveal) return;
    navigator.clipboard.writeText(reveal.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            API Tokens
          </h1>
          <p className="text-sm text-muted-foreground">
            Machine-to-machine credentials. Permissions are restricted
            at use-time to the intersection of token + your own permissions.
          </p>
        </div>
        <Button
          className="bg-[#ff6d00] text-white hover:bg-[#ff6d00]/90"
          onClick={() => setOpen(true)}
        >
          <KeyRoundIcon className="mr-1 h-4 w-4" />
          Create API Token
        </Button>
      </div>

      {/* isEmpty is handled here rather than by the in-table row, so "the fetch
          succeeded and you hold no tokens" and "the fetch failed" can never
          render as the same sentence. */}
      <QueryState
        isLoading={tokensLoading}
        isError={tokensError}
        error={tokensErrorObj}
        label="API tokens"
        onRetry={() => void refetch()}
        isEmpty={tokens.length === 0}
        emptyMessage="No API tokens yet."
      >
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-right">Permissions</th>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-3 py-2 text-left">Last Used</th>
                <th className="px-3 py-2 text-left">Expires</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                  <tr key={t.id} className="border-t border-border">
                    <td className="px-3 py-2 font-medium">{t.name}</td>
                    <td className="px-3 py-2 text-right" title={t.permissions.join(", ")}>
                      {t.permissions.length}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {formatTimestamp(t.created_at, "datetime")}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {t.last_used_at
                        ? formatTimestamp(t.last_used_at, "relative")
                        : "Never"}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {t.expires_at
                        ? formatTimestamp(t.expires_at, "date")
                        : "Never"}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={t.revoked ? "error" : "online"}>
                        {t.revoked ? "Revoked" : "Active"}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {!t.revoked && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            if (
                              confirm(
                                "Revoke this token? Any integrations using it will stop working.",
                              )
                            )
                              revoke.mutate(t.id);
                          }}
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      </QueryState>

      {open && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <p className="font-semibold">Create API Token</p>
            <div>
              <Label>Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. ci-deploy"
              />
            </div>
            <div className="space-y-2">
              <Label>Permissions</Label>
              {Object.entries(grouped).map(([cat, rows]) => (
                <div key={cat} className="rounded border border-border p-2">
                  <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
                    {cat}
                  </p>
                  <div className="grid gap-1 md:grid-cols-2">
                    {rows.map((r) => (
                      <label
                        key={r.key}
                        className="flex items-start gap-2 text-xs"
                      >
                        <input
                          type="checkbox"
                          checked={perms.has(r.key)}
                          onChange={() => toggle(r.key)}
                        />
                        <span>
                          <span className="font-mono">{r.key}</span>
                          <span className="block text-muted-foreground">
                            {r.description}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div>
              <Label>Expires (optional)</Label>
              <Input
                type="date"
                value={expires}
                onChange={(e) => setExpires(e.target.value)}
              />
            </div>
            {error && (
              <p className="rounded bg-red-500/10 px-2 py-1 text-xs text-red-600">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={create.isPending}>
                Create Token
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {reveal && (
        <Card className="border-amber-500/40">
          <CardContent className="space-y-3 p-4">
            <p className="font-semibold text-amber-700">
              ⚠ Copy this token now. It will never be shown again.
            </p>
            <p className="text-sm">{reveal.name}</p>
            <code className="block w-full select-all break-all rounded bg-muted/40 p-2 font-mono text-xs">
              {reveal.token}
            </code>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={copyReveal}>
                <CopyIcon className="mr-1 h-3 w-3" />
                {copied ? "Copied!" : "Copy Token"}
              </Button>
              <Button
                onClick={() => {
                  setReveal(null);
                  refetch();
                }}
              >
                I have saved this token
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
